import { useEffect, useRef } from "react";
import type { Deck } from "./engine";
import { gridLines } from "./grid";

/**
 * Two strips of one track. The upper is a few seconds either side of the
 * playhead, scrolling as the deck plays, with the beat grid drawn over it and
 * every bar line heavier; the lower is the whole track, and a click on it
 * seeks. Both are canvases redrawn every frame from the deck itself -- its
 * time, its data -- rather than from React state, because sixty renders a
 * second is not what React is for and the deck already knows where it is.
 */
export function Waveform({ deck, zoomSeconds = 8 }: { deck: Deck; zoomSeconds?: number }) {
  const zoom = useRef<HTMLCanvasElement>(null);
  const over = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let frame = 0;
    const draw = () => {
      frame = requestAnimationFrame(draw);
      const z = zoom.current, o = over.current;
      if (!z || !o) return;
      drawZoom(z, deck, zoomSeconds);
      drawOverview(o, deck);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [deck, zoomSeconds]);

  return (
    <div className="wave">
      <canvas ref={zoom} className="wave-zoom" />
      <canvas ref={over} className="wave-over"
              onClick={(e) => {
                if (!deck.data) return;
                const r = e.currentTarget.getBoundingClientRect();
                deck.seek(((e.clientX - r.left) / r.width) * deck.data.dur);
              }} />
    </div>
  );
}

/** Size the bitmap to the element at the device's pixel density. */
function fit(c: HTMLCanvasElement): { ctx: CanvasRenderingContext2D; w: number; h: number } | null {
  const dpr = window.devicePixelRatio || 1;
  const w = c.clientWidth, h = c.clientHeight;
  if (w === 0 || h === 0) return null;
  if (c.width !== Math.round(w * dpr) || c.height !== Math.round(h * dpr)) {
    c.width = Math.round(w * dpr);
    c.height = Math.round(h * dpr);
  }
  const ctx = c.getContext("2d");
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { ctx, w, h };
}

/** The loudest bucket between two moments of track time, 0..1. */
function peakBetween(peaks: Uint8Array, hz: number, from: number, to: number): number {
  let a = Math.max(0, Math.floor(from * hz));
  const b = Math.min(peaks.length, Math.max(a + 1, Math.ceil(to * hz)));
  let m = 0;
  for (; a < b; a++) {
    const v = peaks[a] ?? 0;
    if (v > m) m = v;
  }
  return m / 255;
}

const PLAYED = "rgba(99,178,255,.45)";
const AHEAD = "rgba(99,178,255,.9)";
const BEAT = "rgba(255,255,255,.22)";
const BAR = "rgba(240,184,102,.75)";
const HEAD = "#f0b866";

function drawZoom(c: HTMLCanvasElement, deck: Deck, zoomSeconds: number) {
  const f = fit(c);
  if (!f) return;
  const { ctx, w, h } = f;
  ctx.clearRect(0, 0, w, h);
  const d = deck.data;
  const t = deck.time();
  const spp = zoomSeconds / w; // seconds per pixel
  const mid = h / 2;
  if (d) {
    const from = t - zoomSeconds / 2;
    for (let x = 0; x < w; x++) {
      const a = from + x * spp;
      if (a + spp < 0 || a > d.dur) continue;
      const v = peakBetween(d.peaks, d.peaksHz, a, a + spp);
      const half = Math.max(0.5, v * (mid - 2));
      ctx.fillStyle = x < w / 2 ? PLAYED : AHEAD;
      ctx.fillRect(x, mid - half, 1, half * 2);
    }
    if (d.grid) {
      for (const line of gridLines(from, from + zoomSeconds, d.grid, deck.barShift)) {
        const x = Math.round((line.t - from) / spp) + 0.5;
        ctx.strokeStyle = line.bar ? BAR : BEAT;
        ctx.lineWidth = line.bar ? 1.5 : 1;
        ctx.beginPath();
        ctx.moveTo(x, line.bar ? 0 : h * 0.15);
        ctx.lineTo(x, line.bar ? h : h * 0.85);
        ctx.stroke();
      }
    }
  }
  // The playhead, always, so an empty deck still shows where its centre is.
  ctx.fillStyle = HEAD;
  ctx.fillRect(Math.round(w / 2) - 1, 0, 2, h);
}

function drawOverview(c: HTMLCanvasElement, deck: Deck) {
  const f = fit(c);
  if (!f) return;
  const { ctx, w, h } = f;
  ctx.clearRect(0, 0, w, h);
  const d = deck.data;
  if (!d || d.dur <= 0) return;
  const spp = d.dur / w;
  const t = deck.time();
  const head = Math.round(t / spp);
  const mid = h / 2;
  for (let x = 0; x < w; x++) {
    const v = peakBetween(d.peaks, d.peaksHz, x * spp, (x + 1) * spp);
    const half = Math.max(0.5, v * (mid - 1));
    ctx.fillStyle = x < head ? PLAYED : AHEAD;
    ctx.fillRect(x, mid - half, 1, half * 2);
  }
  ctx.fillStyle = HEAD;
  ctx.fillRect(head - 1, 0, 2, h);
}
